import React, { useState } from 'react';
import { XCircle, X, Loader2, AlertTriangle } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  workspaceName: string;
  targetEmail: string;
  onCancelled: () => void;
}

const CancelTransferModal: React.FC<Props> = ({
  open,
  onClose,
  token,
  workspaceName,
  targetEmail,
  onCancelled,
}) => {
  const { showSuccess, showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await apiService.post(`/transfers/${token}/cancel`, {});
      showSuccess('Transfer cancelled successfully');
      onCancelled();
      onClose();
    } catch (err: any) {
      showError(err.response?.data?.error || err.message || 'Failed to cancel transfer');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid var(--v2-border)',
    maxWidth: '440px',
    width: '90%',
    overflow: 'hidden',
    fontFamily: 'var(--font-sans)',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid var(--v2-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid var(--v2-border)',
    backgroundColor: 'var(--v2-surface-alt)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  };

  const getSecondaryBtnStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: hovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  const getDangerBtnStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? '#dc2626' : 'var(--v2-error)',
    color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s',
    minWidth: '140px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  });

  const infoBoxStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    marginBottom: '20px',
  };

  const warningBoxStyle: React.CSSProperties = {
    padding: '14px 16px',
    backgroundColor: 'var(--v2-warning-bg)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #fcd34d',
    display: 'flex',
    gap: '12px',
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-error-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <XCircle size={20} strokeWidth={1.5} color="var(--v2-error)" />
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--v2-text)',
                  margin: 0,
                  letterSpacing: '-0.025em',
                }}
              >
                Cancel Transfer
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '2px 0 0 0' }}>
                {workspaceName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--v2-text-muted)',
              transition: 'all 0.15s',
              opacity: submitting ? 0.5 : 1,
            }}
            onMouseOver={(e) => {
              if (!submitting) {
                e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                e.currentTarget.style.color = 'var(--v2-text)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--v2-text-muted)';
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          <div style={infoBoxStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Workspace</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{workspaceName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Pending transfer to</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)', fontFamily: 'var(--font-mono)' }}>
                {targetEmail}
              </span>
            </div>
          </div>

          <div style={warningBoxStyle}>
            <AlertTriangle size={20} strokeWidth={1.5} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
            <p style={{ fontSize: '0.8125rem', color: '#78350f', margin: 0, lineHeight: 1.6 }}>
              This will cancel the pending transfer. The recipient will no longer be able to accept it. 
              You can initiate a new transfer anytime.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button
            onClick={onClose}
            disabled={submitting}
            onMouseEnter={() => setCancelHovered(true)}
            onMouseLeave={() => setCancelHovered(false)}
            style={{
              ...getSecondaryBtnStyle(cancelHovered),
              opacity: submitting ? 0.5 : 1,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Keep Transfer
          </button>
          <button
            onClick={handleCancel}
            disabled={submitting}
            onMouseEnter={() => setConfirmHovered(true)}
            onMouseLeave={() => setConfirmHovered(false)}
            style={getDangerBtnStyle(confirmHovered, submitting)}
          >
            {submitting && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
            {submitting ? 'Cancelling...' : 'Cancel Transfer'}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CancelTransferModal;
